import { NumberInput, createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useMemo, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { match } from 'ts-pattern';

import { CurrencyConvertor } from '~/components/utils/formatters';
import { Avatar } from '~/domains/design-system/Avatar';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import { Title } from '~/domains/design-system/Title';

import {
  TOTAL_BPS,
  bpsToPercent,
  exactPriceCents,
  organizerPriceCents,
  percentToBps,
  validateShares,
  type ShareValidationError,
} from '../../utils/groupShareMath';

import { GroupUserRow } from './GroupUserRow';
import type { GroupContestInfo, GroupRecipientDraft } from './types';

import dt from '../../testing.const';

// The lowest share a recipient can hold — matches the validation floor.
const MIN_SHARE_PERCENT = 1;
const MIN_SHARE_BPS = MIN_SHARE_PERCENT * 100;

const useStyles = createStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  scrollArea: {
    flex: '0 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: `0 ${theme.other.spacing._24}px`,
  },
  heading: {
    margin: `${theme.other.spacing._16}px 0 0`,
  },
  subtitle: {
    display: 'block',
    color: theme.colors.gray2[3],
    marginBottom: theme.other.spacing._12,
  },
  summaryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._12,
    padding: theme.other.spacing._16,
    background: theme.colors.gray2[0],
    borderRadius: theme.radius.md,
    marginBottom: theme.other.spacing._8,
  },
  summaryText: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  summaryContestName: {
    color: theme.colors.gray2[3],
  },
  summaryFee: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  summaryFeeLabel: {
    color: theme.colors.gray2[3],
  },
  editableValue: {
    width: 100,

    '& input': {
      textAlign: 'right',
      textDecoration: 'underline',
      color: theme.colors.gray2[4],
      minHeight: 'unset',
      height: 24,
      padding: 0,
    },
  },
  priceValue: {
    width: 80,
    textAlign: 'right',
    color: theme.colors.gray2[4],
  },
  mutedValue: {
    color: theme.colors.gray2[3],
  },
  footer: {
    padding: theme.other.spacing._16,
  },
  summaryError: {
    color: theme.colors.red[5],
    display: 'block',
    textAlign: 'center',
    marginBottom: theme.other.spacing._8,
  },
}));

type SetupGroupStepProps = {
  entryName?: string;
  contest: GroupContestInfo;
  organizerHandle?: string;
  organizerAvatarUrl?: string;
  recipients: GroupRecipientDraft[];
  onRecipientsChange: (recipients: GroupRecipientDraft[]) => void;
  onContinue: () => void;
};

export function SetupGroupStep({
  entryName,
  contest,
  organizerHandle,
  organizerAvatarUrl,
  recipients,
  onRecipientsChange,
  onContinue,
}: SetupGroupStepProps) {
  const { t } = useTranslation('group');
  const { classes, cx } = useStyles();

  const validation = useMemo(
    () => validateShares(recipients.map((recipient) => recipient.shareBps)),
    [recipients]
  );

  const organizerNetCostCents = organizerPriceCents(
    recipients.map((recipient) => recipient.priceCents),
    contest.entryFeeCents
  );
  // Hop the caret over the trailing "%" so backspace deletes digits, not the re-added suffix.
  const handlePercentKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Shares are whole percents: reject non-digit printable keys; named keys and modifier combos pass.
    const isModifierCombo = event.ctrlKey || event.metaKey || event.altKey;
    if (!isModifierCombo && event.key.length === 1 && !/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (event.key !== 'Backspace') return;
    const input = event.currentTarget;
    const caret = input.selectionStart;
    if (
      caret !== null &&
      caret === input.selectionEnd &&
      caret === input.value.length &&
      input.value.endsWith('%')
    ) {
      input.setSelectionRange(caret - 1, caret - 1);
    }
  };

  const handlePercentChange = (index: number, percent: number | '' | undefined) => {
    const nextRecipients = [...recipients];
    // Mantine's NumberInput emits `undefined` on clear; floor that (and a typed 0) to the 1% minimum.
    const hasPercent = typeof percent === 'number' && Number.isFinite(percent);
    const shareBps = hasPercent ? Math.max(MIN_SHARE_BPS, percentToBps(percent)) : MIN_SHARE_BPS;

    nextRecipients[index] = {
      ...nextRecipients[index],
      shareBps,
      priceCents: exactPriceCents(shareBps, contest.entryFeeCents),
    };
    onRecipientsChange(nextRecipients);
  };

  // Over-allocated recipient shares drive the derived remainder negative;
  // the field floors at 0% while the sum error owns saying why.
  const organizerDisplayBps = Math.max(0, validation.organizerBps);

  // Mantine keeps the typed string when the controlled value does not change
  // (e.g. an input that clamps back to the current share), so a blur that
  // leaves the field out of sync remounts it onto the derived value.
  const [organizerInputEpoch, setOrganizerInputEpoch] = useState(0);
  const handleOrganizerBlur = (event: FocusEvent<HTMLInputElement>) => {
    const shown = Number(event.currentTarget.value.replace(/%/g, ''));
    if (shown !== bpsToPercent(organizerDisplayBps)) {
      setOrganizerInputEpoch((epoch) => epoch + 1);
    }
  };

  const handleOrganizerPercentChange = (percent: number | '' | undefined) => {
    const hasPercent = typeof percent === 'number' && Number.isFinite(percent);
    if (!hasPercent) return;
    // Every keystroke commits to a real split: values whose remainder cannot
    // give each recipient the 1% floor clamp to the highest organizer share
    // that can, so the field can never display a state the panel would not
    // submit (and live validation covers everything representable).
    const organizerBps = Math.min(
      percentToBps(percent),
      TOTAL_BPS - recipients.length * MIN_SHARE_BPS
    );
    const remainderBps = Math.max(0, TOTAL_BPS - organizerBps);
    const base = Math.floor(remainderBps / recipients.length / MIN_SHARE_BPS) * MIN_SHARE_BPS;
    let leftover = remainderBps - base * recipients.length;

    onRecipientsChange(
      recipients.map((recipient) => {
        const extra = leftover >= MIN_SHARE_BPS ? MIN_SHARE_BPS : 0;
        leftover -= extra;
        const shareBps = base + extra;
        return {
          ...recipient,
          shareBps,
          priceCents: exactPriceCents(shareBps, contest.entryFeeCents),
        };
      })
    );
  };

  // Every validation member maps to copy, so a share error can never disable Continue silently.
  const firstError: ShareValidationError | null =
    validation.organizerError ??
    validation.summaryError ??
    validation.rowErrors.find((error): error is ShareValidationError => error !== null) ??
    null;

  const errorMessage = match(firstError)
    .with('FIFTY_PERCENT_NOT_ALLOWED', () => t('panel.fiftyPercentError'))
    .with('SUM_EXCEEDS_TOTAL', () => t('panel.sumError'))
    .with('SHARE_OUT_OF_RANGE', () => t('panel.minimumShareError'))
    .with('SHARE_NOT_WHOLE_PERCENT', () => t('panel.minimumShareError'))
    .with(null, () => null)
    .exhaustive();

  return (
    <div className={classes.content}>
      <div className={classes.scrollArea}>
        <Title variant="title-x-large" className={classes.heading} tag="h3">
          {t('panel.setUpGroup')}
        </Title>
        <Text variant="title-medium" className={classes.subtitle} component="p">
          {t('panel.setUpGroupSubtitle')}
        </Text>

        <div className={classes.summaryCard}>
          <Avatar handle={organizerHandle ?? ''} src={organizerAvatarUrl} size={32} />
          <div className={classes.summaryText}>
            {entryName !== undefined && <Text variant="subhead-small">{entryName}</Text>}
            <Text variant="body-medium" className={classes.summaryContestName}>
              {contest.name}
            </Text>
          </div>
          <div className={classes.summaryFee}>
            <Text variant="subhead-small">{CurrencyConvertor(contest.entryFeeCents / 100)}</Text>
            <Text variant="body-medium" className={classes.summaryFeeLabel}>
              {t('panel.entryFee')}
            </Text>
          </div>
        </div>

        <GroupUserRow
          handle={organizerHandle ?? ''}
          label={t('panel.you')}
          avatarUrl={organizerAvatarUrl}
          rightSection={
            <>
              <NumberInput
                className={classes.editableValue}
                variant="unstyled"
                hideControls
                key={organizerInputEpoch}
                min={1}
                max={99}
                maxLength={3}
                value={bpsToPercent(organizerDisplayBps)}
                onChange={handleOrganizerPercentChange}
                onKeyDown={handlePercentKeyDown}
                onBlur={handleOrganizerBlur}
                formatter={(value) => (value ? `${value}%` : '')}
                parser={(value) => value?.replace(/%/g, '')}
                data-test-id={dt.panel.organizerPercentInput}
              />
              <Text
                variant="subhead-small"
                className={cx(classes.priceValue, {
                  [classes.mutedValue]: organizerNetCostCents === 0,
                })}
                data-test-id={dt.panel.organizerPrice}
              >
                {CurrencyConvertor(organizerNetCostCents / 100)}
              </Text>
            </>
          }
        />

        {recipients.map((recipient, index) => (
          <GroupUserRow
            key={recipient.userId}
            handle={recipient.handle}
            avatarUrl={recipient.avatarUrl}
            rightSection={
              <>
                <NumberInput
                  className={classes.editableValue}
                  variant="unstyled"
                  hideControls
                  min={MIN_SHARE_PERCENT}
                  max={99}
                  maxLength={3}
                  value={bpsToPercent(recipient.shareBps)}
                  onChange={(value) => handlePercentChange(index, value)}
                  onKeyDown={handlePercentKeyDown}
                  formatter={(value) => (value ? `${value}%` : '')}
                  parser={(value) => value?.replace(/%/g, '')}
                  data-test-id={dt.panel.percentInput}
                />
                <Text
                  variant="subhead-small"
                  className={cx(classes.priceValue, {
                    [classes.mutedValue]: recipient.priceCents === 0,
                  })}
                  data-test-id={dt.panel.price}
                >
                  {CurrencyConvertor(recipient.priceCents / 100)}
                </Text>
              </>
            }
          />
        ))}
      </div>

      <div className={classes.footer}>
        {errorMessage && (
          <Text variant="body-medium" className={classes.summaryError} component="p">
            {errorMessage}
          </Text>
        )}
        <Button
          fullWidth
          variant="primary-fill"
          size="large"
          disabled={!validation.isValid}
          onClick={onContinue}
          data-test-id={dt.panel.continueToPickControl}
        >
          {t('panel.continueToPickControl')}
        </Button>
      </div>
    </div>
  );
}
